import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Content,
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
  Tooltip,
  Select,
  SelectItem,
} from '@carbon/react';
import {
  Add,
  TrashCan,
  Launch,
  Share,
  Catalog,
  Search,
  Renew,
  CheckmarkFilled,
  ErrorFilled,
  Time,
  ListBulleted,
  Tag as TagIcon,
} from '@carbon/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { explorationApi } from '@/lib/api/exploration.api';
import { CreateExplorationModal } from '@/components/knowledge-explorer/CreateExplorationModal';
import { ShareExplorationModal } from '@/components/knowledge-explorer/ShareExplorationModal';
import { ClaimsFacet } from '@/components/knowledge-search/ClaimsFacet';
import { EntitiesFacet } from '@/components/knowledge-search/EntitiesFacet';
import { SemanticFacet } from '@/components/knowledge-search/SemanticFacet';
import { useKnowledgeBases } from '@/lib/query/hooks';
import type { KnowledgeExploration } from '@snxt/snxt-onto-share';
import { ExplorationStatus } from '@/components/knowledge-explorer/ExplorationStatus';
import '@/components/knowledge-search/FacetSearch.scss';
import './KnowledgeExplorerListPage.scss';

// ── Mode toggle ───────────────────────────────────────────────────────────────

type PageMode = 'ontology' | 'facet';

const MODES: Array<{ value: PageMode; label: string; icon: typeof Catalog; description: string }> = [
  {
    value: 'ontology',
    label: 'Ontology Explorer',
    icon: Catalog,
    description: 'Populate ontology concepts with evidence from your knowledge bases',
  },
  {
    value: 'facet',
    label: 'Facet Search',
    icon: Search,
    description: 'Search claims, entities and ask semantic questions across your knowledge bases',
  },
];

// ── Facet search sub-tabs ─────────────────────────────────────────────────────

type FacetTab = 'claims' | 'entities' | 'semantic';

const FACET_TABS: Array<{ value: FacetTab; label: string; icon: typeof ListBulleted; title: string }> = [
  { value: 'claims',   label: 'Claims',   icon: ListBulleted, title: 'Search triplets (subject–predicate–object) extracted by L3' },
  { value: 'entities', label: 'Entities', icon: TagIcon,      title: 'Search named entities extracted by L3' },
  { value: 'semantic', label: 'Semantic', icon: Search,       title: 'Ask a question — vector similarity search over KU embeddings' },
];

// ── Status helpers ────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed': return <CheckmarkFilled size={14} style={{ color: 'var(--cds-support-success, #24a148)' }} />;
    case 'failed':    return <ErrorFilled     size={14} style={{ color: 'var(--cds-support-error, #da1e28)' }} />;
    case 'running':   return <Renew           size={14} style={{ color: 'var(--cds-support-info, #0043ce)', animation: 'spin 1s linear infinite' }} />;
    default:          return <Time            size={14} style={{ color: 'var(--cds-text-secondary)' }} />;
  }
}

/**
 * A small radial progress ring for the grounded/total ratio.
 * r=8 → circumference ≈ 50.27 px
 */
function GroundingRing({ grounded, total }: { grounded: number; total: number }) {
  if (total === 0) return null;
  const pct = grounded / total;
  const r = 8;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
      <circle cx={10} cy={10} r={r} fill="none" stroke="var(--cds-border-subtle-01)" strokeWidth={2} />
      <circle
        cx={10} cy={10} r={r}
        fill="none"
        stroke="var(--cds-support-success, #24a148)"
        strokeWidth={2}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        transform="rotate(-90 10 10)"
      />
    </svg>
  );
}

// ── Inline Facet Search panel ─────────────────────────────────────────────────

function FacetSearchPanel() {
  const [kbId, setKbId] = useState('');
  const [activeTab, setActiveTab] = useState<FacetTab>('claims');
  const { data: kbs, isLoading: kbsLoading } = useKnowledgeBases();

  return (
    <div className="facet-search">
      {/* ── Top bar: KB selector + mode tabs ── */}
      <div className="facet-search__topbar">
        <div className="facet-search__kb-select">
          {kbsLoading ? (
            <InlineLoading description="Loading knowledge bases…" />
          ) : (
            <Select
              id="facet-kb-select"
              labelText="Knowledge base"
              value={kbId}
              onChange={(e) => setKbId(e.target.value)}
            >
              <SelectItem value="" text="Select a knowledge base…" />
              {(kbs ?? []).map((kb) => (
                <SelectItem key={kb.id} value={kb.id} text={kb.name} />
              ))}
            </Select>
          )}
        </div>

        <div className="facet-search__tabs">
          {FACET_TABS.map(({ value, label, icon: Icon, title }) => (
            <button
              key={value}
              className={`facet-search__tab${activeTab === value ? ' facet-search__tab--active' : ''}`}
              title={title}
              onClick={() => setActiveTab(value)}
            >
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Main content ── */}
      {!kbId ? (
        <div className="facet-search__empty-state">
          <Catalog size={48} />
          <p>Select a knowledge base above to start searching.</p>
        </div>
      ) : (
        <>
          {activeTab === 'claims'   && <ClaimsFacet   kbId={kbId} />}
          {activeTab === 'entities' && <EntitiesFacet kbId={kbId} />}
          {activeTab === 'semantic' && <SemanticFacet kbId={kbId} />}
        </>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function KnowledgeExplorerListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState<{ id: string; name: string } | null>(null);
  const [mode, setMode] = useState<PageMode>('ontology');
  const { data: explorations, isLoading, isError } = useQuery<KnowledgeExploration[]>({
    queryKey: ['explorations'],
    queryFn: () => explorationApi.list(),
    refetchInterval: (query) => {
      const list = query.state.data as KnowledgeExploration[] | undefined;
      if (!list) return false;
      return list.some(
        (e) => e.populate_status === 'running' || e.populate_status === 'pending'
      )
        ? 5_000
        : false;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => explorationApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['explorations'] }),
  });

  const handleCreated = (id: string) => {
    setCreateOpen(false);
    navigate(`/explorer/${id}`);
  };

  return (
    <Content className="app-content">
      <div className="ke-list-page">

        {/* ── Page header ── */}
        <div className="ke-list-page__header">
          <div>
            <h1 className="ke-list-page__title">Knowledge Explorer</h1>
            <p className="ke-list-page__subtitle">
              Explore knowledge from two angles: map it onto your ontology, or search it directly.
            </p>
          </div>

          {/* Mode toggle */}
          <div className="ke-list-page__mode-toggle">
            {MODES.map(({ value, label, icon: Icon, description }) => (
              <button
                key={value}
                title={description}
                className={`ke-list-page__mode-btn${mode === value ? ' ke-list-page__mode-btn--active' : ''}`}
                onClick={() => setMode(value)}
              >
                <Icon size={16} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {mode === 'ontology' && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button renderIcon={Add} onClick={() => setCreateOpen(true)}>
                New exploration
              </Button>
            </div>
          )}
        </div>

        {/* ── Facet Search panel (when mode = facet) ── */}
        {mode === 'facet' && <FacetSearchPanel />}

        {/* ── Ontology Explorer content (when mode = ontology) ── */}
        {mode === 'ontology' && (
          <>
            {/* ── State feedback ── */}
            {isLoading && <InlineLoading description="Loading explorations…" />}
            {isError && (
              <InlineNotification
                kind="error"
                title="Failed to load explorations"
                hideCloseButton
              />
            )}

            {/* ── Empty state ── */}
            {!isLoading && !isError && (explorations ?? []).length === 0 && (
              <div className="ke-list-page__empty">
                <Catalog size={48} style={{ opacity: 0.3 }} />
                <p>No explorations yet. Create one to start mapping your KB against an ontology.</p>
                <Button kind="ghost" renderIcon={Add} onClick={() => setCreateOpen(true)}>
                  Create your first exploration
                </Button>
              </div>
            )}

            {/* ── Card grid ── */}
            {!isLoading && !isError && (explorations ?? []).length > 0 && (
              <div className="ke-list-page__grid">
                {(explorations ?? []).map((e) => {
                  const pct = e.concepts_total && e.concepts_total > 0
                    ? Math.round((e.concepts_grounded ?? 0) / e.concepts_total * 100)
                    : null;
                  const isRunning = e.populate_status === 'running' || e.populate_status === 'pending';

                  return (
                    <div key={e.id} className={`ke-card${isRunning ? ' ke-card--running' : ''}`}>

                      {/* Status strip at top */}
                      <div className="ke-card__status-strip">
                        <StatusIcon status={e.populate_status} />
                        <ExplorationStatus status={e.populate_status} error={e.populate_error} />
                        {isRunning && <InlineLoading className="ke-card__running-spinner" />}
                      </div>

                      <div className="ke-card__header">
                        <span className="ke-card__name">{e.name}</span>
                      </div>

                      <div className="ke-card__meta">
                        <Tag size="sm" type="outline">
                          {e.ontology_name ?? e.ontology_id}
                        </Tag>
                        <Tag size="sm" type="outline">
                          v{e.ontology_version_number ?? '?'}
                        </Tag>
                        <Tag size="sm" type="outline">
                          KB: {e.knowledge_base_name ?? e.knowledge_base_id}
                        </Tag>
                      </div>

                      {/* Progress row: ring + bar + label */}
                      {e.concepts_grounded != null && e.concepts_total != null && (
                        <div className="ke-card__progress-row">
                          <Tooltip
                            label={`${e.concepts_grounded} of ${e.concepts_total} concepts grounded (${pct ?? 0}%)`}
                            align="bottom"
                          >
                            <div className="ke-card__progress-ring-wrap" tabIndex={0}>
                              <GroundingRing
                                grounded={e.concepts_grounded ?? 0}
                                total={e.concepts_total}
                              />
                            </div>
                          </Tooltip>
                          <div className="ke-card__progress-bar-col">
                            <div className="ke-card__progress-bar-track">
                              <div
                                className="ke-card__progress-bar-fill"
                                style={{ width: `${pct ?? 0}%` }}
                              />
                            </div>
                            <span className="ke-card__progress-label">
                              {e.concepts_grounded}/{e.concepts_total} concepts grounded
                              {pct !== null && ` (${pct}%)`}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Instances count */}
                      {e.instances_total != null && e.instances_total > 0 && (
                        <div className="ke-card__instances">
                          <Tag size="sm" type="teal">
                            {e.instances_total} instance{e.instances_total !== 1 ? 's' : ''}
                          </Tag>
                        </div>
                      )}

                      <div className="ke-card__actions">
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={Launch}
                          onClick={() => navigate(`/explorer/${e.id}`)}
                        >
                          Open
                        </Button>
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={Share}
                          hasIconOnly
                          iconDescription="Share (read-only)"
                          onClick={() => setShareTarget({ id: e.id, name: e.name })}
                        />
                        <Button
                          kind="danger--ghost"
                          size="sm"
                          renderIcon={TrashCan}
                          hasIconOnly
                          iconDescription="Delete"
                          onClick={() => {
                            if (window.confirm(`Delete "${e.name}"?`)) {
                              deleteMutation.mutate(e.id);
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {shareTarget && (
          <ShareExplorationModal
            open={!!shareTarget}
            onClose={() => setShareTarget(null)}
            explorationId={shareTarget.id}
            explorationName={shareTarget.name}
          />
        )}

        <CreateExplorationModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      </div>
    </Content>
  );
}

// Made with Bob
